import { Serialiser } from "hooks/serialisers/types";
import { useQueryParamState } from "hooks/useQueryParamState";
import { ModelPredictionDataPoint } from "src/store/api/generatedApi";

export const OVERLAY_STAGES = ["raw", "smoothed", "peaks", "argmax"] as const;

export type OverlayStage = (typeof OVERLAY_STAGES)[number];

const QUERY_PARAM_KEY = "stage";

// No validation needed — usePostProcessingOverlay clamps the parsed value to an
// available stage, so an unknown URL value can never take effect.
const stageSerialiser: Serialiser<OverlayStage, never> = {
  format: (stage) => stage,
  parse: (text) => ({ valid: true, value: text as OverlayStage }),
};

const STAGE_AVAILABILITY: [
  OverlayStage,
  (point: ModelPredictionDataPoint) => boolean,
][] = [
  ["smoothed", (point) => point.postprocessed_probability !== null],
  ["peaks", (point) => point.peak !== null],
  ["argmax", (point) => point.postprocessed_flags.includes("argmax")],
];

export const availableStagesForSeries = (
  series: ModelPredictionDataPoint[]
): Set<OverlayStage> =>
  new Set<OverlayStage>([
    "raw",
    ...STAGE_AVAILABILITY.filter(([, hasSignal]) => series.some(hasSignal)).map(
      ([stage]) => stage
    ),
  ]);

const defaultStage = (availableStages: Set<OverlayStage>): OverlayStage =>
  availableStages.has("smoothed") ? "smoothed" : "raw";

export const usePostProcessingOverlay = (
  availableStages: Set<OverlayStage>
) => {
  const fallbackStage = defaultStage(availableStages);
  const [stage, setStage] = useQueryParamState(
    QUERY_PARAM_KEY,
    stageSerialiser,
    fallbackStage
  );

  const activeStage = availableStages.has(stage) ? stage : fallbackStage;

  return { activeStage, selectStage: setStage };
};
